import { ApiResponse } from './apiResponse';
import type { ExperienceGroup } from './profile/userExperiences';
import type {
  GameDistributionStat,
  MonthlyActivityStat,
  MonthlyOrganizerActivityStat,
  UserProfileStats,
} from './profile/profileStats';
import type { SocialLink } from './socialLinks';

export enum Gender {
  MALE = 'male',
  FEMALE = 'female',
  OTHER = 'other',
  UNSPECIFIED = '',
}

/** Identities only — flow order lives in `ONBOARDING_STEPS`, never in these numbers. */
export enum CompleteProfileStep {
  TERMS = 1,
  PROFILE = 2,
  PREFERENCE = 3,
  TWO_FA = 4,
}
export interface LoginRequest {
  email: string;
  password: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  acceptTerms: boolean;
  isSSO: boolean;
  typeOfLogin: string[];
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ProfilePicture {
  url: string;
  name: string;
  size: number;
  type: string;
}

export interface CountryCode {
  name: string;
  code: string;
  abbreviation: string;
  number: string;
}

export interface DiscordProfile {
  id: string;
  discordUsername: string;
  name: string;
  verified: boolean;
  email: string;
  profilePicture: string;
}

export interface UserStats {
  identityRoles?: string[];
  experience?: ExperienceGroup[];
  socialLinks?: SocialLink[];
  stats?: UserProfileStats;
  monthlyActivity?: Record<string, MonthlyActivityStat>;
  monthlyOrganizerActivity?: Record<string, MonthlyOrganizerActivityStat>;
  gameDistribution?: Record<string, GameDistributionStat>;
  lastComputedAt?: string | null;
}

export interface User {
  email: string;
  username: string;
  name: string;
  typeOfLogin: string[];
  isSSO: boolean;
  profilePicture: ProfilePicture;
  coverImage?: ProfilePicture;
  isDeleted: boolean;
  isEmailVerified: boolean;
  ssouid: string[];
  isUserBanned: boolean;
  countryCode: CountryCode;
  kycVerified: boolean;
  den: any; // eslint-disable-line @typescript-eslint/no-explicit-any
  verificationStatus: string;
  createdAt: string;
  updatedAt: string;
  discordProfile: DiscordProfile;
  is18AndAbove?: boolean;
  id: string;
  gender?: Gender;
  mobileNumber?: string;
  isTwoFAEnabled?: boolean;
  dateOfBirth?: string;
  preferredGameIds?: string[];
  isProfileComplete?: boolean;
  /** Separate gate from the wizard: true blocks the app behind the terms screen. */
  hasPendingConsent?: boolean;
  userStats?: UserStats;
}

export interface LoginResult {
  accessToken: string;
  user: User;
  refreshToken: string;
  requiresTwoFactorSetup?: boolean;
}
export interface ClaimTokenValidationRequest {
  token: string;
}

export interface ClaimTokenValidationResponse {
  valid: boolean;
  userId?: string;
  email?: string;
  teamId?: string;
  reason?: string;
}

export interface SetPasswordWithClaimTokenRequest {
  token: string;
  password: string;
}

export type LoginResponse = ApiResponse<LoginResult>;
export type SignupResponse = ApiResponse<LoginResult>;
export type ForgotPasswordResponse = ApiResponse<string>;
export type ResetPasswordResponse = ApiResponse<string>;
export type ClaimTokenValidationResponseType =
  ApiResponse<ClaimTokenValidationResponse>;
export type SetPasswordWithClaimTokenResponse = ApiResponse<string>;

// Two-Factor Authentication Types
export interface TwoFactorSecretResponse {
  secret: string;
  qrCode: string;
}

export interface VerifyAndEnableTwoFactorRequest {
  secret: string;
  token: string;
}

// SkipTwoFactorRequest - No body needed, uses current user from token
export type SkipTwoFactorRequest = Record<string, never>;

export interface DisableTwoFactorRequest {
  token: string;
}

export interface VerifyTwoFactorTokenRequest {
  token: string;
}

export type TwoFactorSecretResponseType = ApiResponse<TwoFactorSecretResponse>;
export type VerifyAndEnableTwoFactorResponse = ApiResponse<string>;
export type VerifyTwoFactorTokenResponse = ApiResponse<{ verified: boolean }>;
export type SkipTwoFactorResponse = ApiResponse<{ skipped: boolean }>;
export type DisableTwoFactorResponse = ApiResponse<string>;

// Sequence WaaS Authentication Types
export interface SequenceWaaSLoginRequest {
  sequenceToken: string; // JWT idToken from Sequence WaaS
  walletAddress?: string; // Optional wallet address
  email?: string; // Optional email if available
}

export interface SequenceWaaSLoginResult {
  accessToken: string;
  user: User;
  refreshToken: string;
  isNewUser: boolean;
  walletAddress?: string;
}

export type SequenceWaaSLoginResponse = ApiResponse<SequenceWaaSLoginResult>;

/** The login method an email is already registered with in Sequence. */
export enum ConflictAccountType {
  GOOGLE = 'google',
  APPLE = 'apple',
  EMAIL = 'email',
  PLAYFAB = 'playfab',
  UNKNOWN = 'unknown',
}

/** Sequence refused a sign-in: this email already belongs to an account of another method. */
export interface EmailConflictInfo {
  email: string;
  accountType: ConflictAccountType;
}

// Get Profile Response Types
export interface GetProfileResult {
  accessToken?: string;
  user?: User;
  refreshToken?: string;
  requiresTwoFactorSetup?: boolean;
}

export type GetProfileResponse = ApiResponse<GetProfileResult>;
